import { effect, inject } from '@angular/core';
import { patchState, signalState, WritableStateSource } from '@ngrx/signals';
import { Subject } from 'rxjs';

import { BrowserWindowService } from '@spaces-ui/browser-window';
import { ServiceBus } from '@spaces-ui/service-bus';

export interface DataStateEvent {
  type: string;
  payload: unknown;
}

export enum DataStateBusEvents {
  RefreshData = 'RefreshData',
  ResetData = 'ResetData',
  BackOnline = 'BackOnline',
}

export type DataStateBusEventPayloads = {
  [DataStateBusEvents.RefreshData]: void;
  [DataStateBusEvents.ResetData]: void;
  [DataStateBusEvents.BackOnline]: void;
};

/**
 * T is the signal state type
 */
export abstract class BaseDataState<T extends object> {
  protected serviceBus = inject(ServiceBus);
  private browserWindowService = inject(BrowserWindowService);

  private _events = new Subject<DataStateEvent>();
  public events = this._events.asObservable();

  protected abstract initialState: T;
  protected abstract state: WritableStateSource<T>;

  private lastOnlineHandledTime = 0;

  public constructor() {
    effect(() => {
      const isOnline = this.browserWindowService.isOnline();

      if (!isOnline) {
        this.lastOnlineHandledTime = 0; // Reset so next BackOnline triggers properly
        this.handleOffline();
      }
    });

    this.serviceBus
      .on<DataStateBusEvents, DataStateBusEventPayloads>(DataStateBusEvents.BackOnline)
      .subscribe(() => {
        // Debounce to prevent rapid re-initialization
        const now = Date.now();
        if (now - this.lastOnlineHandledTime > 2000) {
          this.lastOnlineHandledTime = now;
          this.handleOnline();
        }
      });

    this.serviceBus
      .on<DataStateBusEvents, DataStateBusEventPayloads>(DataStateBusEvents.RefreshData)
      .subscribe(() => this.initData());

    this.serviceBus
      .on<DataStateBusEvents, DataStateBusEventPayloads>(DataStateBusEvents.ResetData)
      .subscribe(() => this.reset());
  }

  public reset() {
    patchState(this.state, this.initialState);
    this.handleOffline();
  }

  protected initState(initialState: T) {
    this.initialState = initialState;
    return signalState(this.initialState);
  }

  protected raiseEvent(type: string, payload: unknown) {
    this._events.next({ type, payload });
  }

  protected abstract initData(): void;
  protected abstract handleOffline(): void;
  protected abstract handleOnline(): void;
}
